import * as React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DevicesIcon from '@mui/icons-material/Devices';
import { useTranslation } from 'react-i18next';

import MainLayout from '../components/layout/Main';
import api from "../api";

function Devices() {
  const [devices, setDevices] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [editedDevice, setEditedDevice] = React.useState({
    name: '',
    serial_number: '',
    user: null
  });
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const { t } = useTranslation();
  const theme = useTheme();

  React.useEffect(() => {
    document.title = t("devices.page_title") || "Devices";
    fetchDevices();
    fetchUsers();
  }, []);

  const fetchDevices = () => {
    setLoading(true);
    api
      .get("/api/devices/all/")
      .then((res) => res.data)
      .then((data) => {
        setDevices(data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setError('Failed to load devices');
        setLoading(false);
      });
  };

  const fetchUsers = () => {
    api
      .get("/api/user/list/")
      .then((res) => res.data)
      .then((data) => {
        setUsers(data);
      })
      .catch((err) => console.log(err));
  };

  const handleEditClick = (device) => {
    setSelectedDevice(device);
    setEditedDevice({
      name: device.name,
      serial_number: device.serial_number,
      user: device.user
    });
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedDevice(null);
    setError('');
    setSuccess('');
  };

  const handleSave = () => {
    if (!editedDevice.name || !editedDevice.serial_number) {
      setError('Name and Serial Number are required');
      return;
    }

    api
      .patch(`/api/devices/${selectedDevice.id}/`, editedDevice)
      .then((res) => {
        setSuccess('Device updated successfully');
        fetchDevices();
        setTimeout(() => {
          handleDialogClose();
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
        setError('Failed to update device');
      });
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      headerClassName: 'datagrid-header',
    },
    { 
      field: 'name', 
      headerName: t("devices.name_column") || 'Name', 
      flex: 1,
      minWidth: 200,
      headerClassName: 'datagrid-header',
    },
    { 
      field: 'serial_number', 
      headerName: t("devices.serial_number_column") || 'Serial Number', 
      flex: 0.8,
      minWidth: 150,
      headerClassName: 'datagrid-header',
    },
    { 
      field: 'folder_name', 
      headerName: t("devices.folder_name_column") || 'Folder Name', 
      flex: 1,
      minWidth: 200,
      headerClassName: 'datagrid-header',
    },
    { 
      field: 'owner', 
      headerName: t("devices.owner_column") || 'Owner', 
      flex: 0.8,
      minWidth: 150,
      headerClassName: 'datagrid-header',
      valueGetter: (value, row) => {
        const userInfo = row.user_info;
        if (userInfo) {
          return `${userInfo.first_name} ${userInfo.last_name}`.trim() || userInfo.username;
        }
        return 'Unassigned';
      }
    },
    {
      field: 'actions',
      headerName: t("devices.actions_column") || 'Actions',
      width: 120,
      headerClassName: 'datagrid-header',
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEditClick(params.row)}
          sx={{ 
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: 'rgba(220, 53, 69, 0.04)'
            }
          }}
        >
          {t("devices.edit_button") || 'Edit'}
        </Button>
      ),
    },
  ];

  return (
    <MainLayout activeComponent='/devices'>
      <Box sx={{ width: '100%' }}>
        <Card 
          variant='outlined' 
          sx={{ 
            borderRadius: '16px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
              <DevicesIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
              <Typography 
                variant='h4' 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("devices.page_title") || 'Devices'}
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box 
                sx={{ 
                  height: 600, 
                  width: '100%',
                  '& .datagrid-header': {
                    backgroundColor: theme.palette.mode === 'light' ? '#f8f9fa' : '#1e293b',
                    fontWeight: 600,
                  }
                }}
              >
                <DataGrid
                  rows={devices}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  disableSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>
            {t("devices.edit_dialog_title") || 'Edit Device'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <TextField
              margin="normal"
              fullWidth
              label={t("devices.name_label") || "Device Name"}
              value={editedDevice.name}
              onChange={(e) => setEditedDevice({ ...editedDevice, name: e.target.value })}
              required
            />
            
            <TextField
              margin="normal"
              fullWidth
              label={t("devices.serial_number_label") || "Serial Number"}
              value={editedDevice.serial_number}
              onChange={(e) => setEditedDevice({ ...editedDevice, serial_number: e.target.value })}
              required
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>{t("devices.owner_label") || "Owner"}</InputLabel>
              <Select
                value={editedDevice.user || ''}
                onChange={(e) => setEditedDevice({ ...editedDevice, user: e.target.value || null })}
                label={t("devices.owner_label") || "Owner"}
              >
                <MenuItem value="">
                  <em>{t("devices.unassigned") || "Unassigned"}</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {`${user.first_name} ${user.last_name}`.trim() || user.username} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={handleDialogClose} color="inherit">
              {t("devices.cancel_button") || "Cancel"}
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={success !== ''}
            >
              {t("devices.save_button") || "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
}

export default Devices;
